import { useRef, useState } from 'react';
import './device-details.styles.scss';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import { API } from 'Amplify';

import validator from '@rjsf/validator-ajv8';
import type { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import type { CustomValidator, ErrorListProps, FormValidation, RJSFValidationError, TemplatesType } from '@rjsf/utils';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon/ListItemIcon';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

const BALANCING_ACTIONS = {
  CANCEL: {
    action: 'cancel',
    label: 'Cancel balancing',
    message: 'You are about to remove all EAN data & FCR allocation & set an opt out flag on this device, are you sure you want to continue?',
    description: 'Removes all EAN data, FCR allocation & set an opt out flag on this device.',
  },
  REMOVE: {
    action: 'remove',
    label: 'Remove EAN',
    message: 'You are about to remove all EAN info for this device, are you sure you want to continue?',
    description: 'Removes all EAN data from this device.',
  },
  RESET: {
    action: 'reset',
    label: 'Reset',
    message: 'You are about to remove the opt out flag for this device, are you sure you want to continue?',
    description: 'Resets the opt out flag for this device.',
  },
};

export type BalancingPartProps = {
  schema: any;
  uiSchema: any;
  formData: any;
  thingName: string;
  optOutFcr: boolean;
  balancing: any;
  isUserAnAdmin: boolean;
};

const customValidate: CustomValidator = (formData, errors, _uiSchema?): FormValidation => {
  if (formData.ean === undefined) {
    return errors;
  }

  if (formData.ean && !formData.ean.match(/^[0-9]{18}$/)) {
    (errors as any).ean.addError('The EAN must be an 18-digit number containing only numeric characters (0–9)');
  }

  return errors;
};

function transformErrors(errors: RJSFValidationError[], _: any): RJSFValidationError[] {
  return errors.map((error) => {
    if (error.name === 'required') {
      error.message = 'You must provide a value for this field';
    }

    return error;
  });
}

const CustomErrorListTemplate = (props: ErrorListProps) => {
  const { errors } = props;

  if (errors.length === 0) {
    return null;
  }

  return (
    <Alert
      icon={false}
      severity="error"
      sx={{ mb: 2 }}
    >
      <List dense>
        {errors.map((error, index) => (
          <ListItem
            disablePadding
            key={index}
            sx={{ py: 0 }}
          >
            <ListItemIcon sx={{ minWidth: '32px' }}>
              <ErrorOutlineOutlinedIcon color="error" />
            </ListItemIcon>
            <ListItemText primary={error.message} />
          </ListItem>
        ))}
      </List>
    </Alert>
  );
};

const customTemplates: Partial<TemplatesType> = {
  ErrorListTemplate: CustomErrorListTemplate,
};

export function BalancingPart(props: BalancingPartProps) {
  const { schema, uiSchema, formData, thingName, optOutFcr, balancing, isUserAnAdmin } = props;

  const [currentData, setCurrentData] = useState(formData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [edit, setEdit] = useState(false);
  const formRef = useRef<any | null>(null);
  const { ean } = formData || {};
  const { fcr, reference } = balancing || {};
  const { bsp } = fcr || {};

  const disabled = loading || (ean && !edit);

  // Must put here some other info regarding where I am in FCR
  // 1. Insert info for FCR - no ean
  // 2. User in waiting list - ean + elia id
  // 3. Info about current vDP - ean + elia id + vdp id

  const onSubmit = async (ev: any) => {
    setLoading(true);
    setError(null);
    try {
      const { formData: body } = ev;
      await API.post('IO_API', `/devices/${thingName}/balancing`, { body });
      setEdit(false);
    } catch (e: any) {
      setError(
        typeof e.response?.data?.error === 'string'
          ? e.response?.data?.error
          : 'An error occurred! If the issue is not resolved after a few retries, please contact support.',
      );
    }
    setLoading(false);
  };

  const onAction = async ({ message, action }: any) => {
    const confirm = window.confirm(message);

    if (!confirm) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await API.del('IO_API', `/devices/${thingName}/balancing?action=${action}`, {});
      setCurrentData(undefined);
    } catch (e: any) {
      setError(
        typeof e.response?.data?.error === 'string'
          ? e.response?.data?.error
          : 'An error occurred! If the issue is not resolved after a few retries, please contact support.',
      );
    }

    setLoading(false);
  };

  const onChange = (ev: IChangeEvent) => {
    setCurrentData(ev.formData);
  };

  const onToggleEdit = () => setEdit((o) => !o);

  const eanActionButton = (action: any, show: any) =>
    show ? (
      <Tooltip title={action.description}>
        <span>
          <Button
            variant="outlined"
            disabled={loading || edit}
            onClick={() => onAction(action)}
          >
            {action.label}
          </Button>
        </span>
      </Tooltip>
    ) : null;

  const adminContent = (showSeparator?: boolean) =>
    isUserAnAdmin ? (
      <>
        {showSeparator && <Box borderRight="solid 1px gray" />}

        {/* Remove */}
        {eanActionButton(BALANCING_ACTIONS.REMOVE, ean)}

        {/* Cancel */}
        {eanActionButton(BALANCING_ACTIONS.CANCEL, !optOutFcr)}

        {/* Reset */}
        {eanActionButton(BALANCING_ACTIONS.RESET, optOutFcr)}
      </>
    ) : null;

  if (optOutFcr) {
    return (
      <Stack spacing={2}>
        <Typography>You are opted out of balancing.</Typography>
        {error ? <Alert severity="error">{error}</Alert> : null}
        <Stack
          spacing={2}
          direction="row"
        >
          {adminContent()}
        </Stack>
      </Stack>
    );
  }

  if (ean && bsp) {
    return (
      <Stack spacing={2}>
        <Typography>Ready to participate</Typography>
        <Typography>EAN: {ean}</Typography>
        {/* reference ? <div>TSO reference: {reference}</div> : null */}
        {/* bsp && admin ? <div>Service provider: {bsp}</div> : null */}
        {error ? <Alert severity="error">{error}</Alert> : null}
        <Stack
          spacing={2}
          direction="row"
        >
          {adminContent()}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {reference ? <Typography>TSO reference: {reference}</Typography> : null}
      <Form
        ref={formRef}
        schema={schema}
        uiSchema={uiSchema}
        validator={validator}
        onChange={onChange}
        formData={currentData}
        disabled={disabled}
        onSubmit={onSubmit}
        extraErrors={error ? ({ Validation: { __errors: [error] } } as any) : {}}
        templates={customTemplates}
        liveValidate={true}
        customValidate={customValidate}
        transformErrors={transformErrors}
      >
        <Stack
          spacing={2}
          direction="row"
        >
          {/* Submit */}
          {(!ean || edit) && (
            <Button
              type="submit"
              disabled={loading || (formRef.current?.state?.schemaValidationErrors?.length ?? 0) > 0}
              variant="contained"
            >
              {loading ? <CircularProgress size={20} /> : 'Submit'}
            </Button>
          )}

          {/* Edit */}
          {ean && !edit && (
            <Button
              variant="contained"
              onClick={onToggleEdit}
            >
              Edit
            </Button>
          )}

          {/* Cancel */}
          {ean && edit && (
            <Button
              variant="outlined"
              onClick={onToggleEdit}
            >
              Cancel
            </Button>
          )}

          {/* Admin button */}
          {adminContent(true)}
        </Stack>
      </Form>
    </Stack>
  );
}
